/* Honeybear Coffee — V9 native header cart integration.
   Native drawer only. Delegated capture prevents competing custom form handlers.
   No redirects, no second mini cart, and no product added during diagnostics.
*/
(() => {
  'use strict';
  if (window.HoneybearNativeCart?.version === 9) return;

  const formSelector = 'form[data-hbl-add], form.hbp3-form';
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
  const base = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const endpoint = path => base() + path;
  const state = {busy: false, last: {stage: 'idle', error: null}, handled: 0};

  function drawer() {
    return document.querySelector('theme-drawer#cart-drawer') ||
      document.querySelector('theme-drawer:has(cart-drawer-component)') ||
      document.querySelector('cart-drawer');
  }
  function drawerIsOpen(element) {
    return Boolean(element && (element.hasAttribute('open') || element.classList.contains('active') || element.querySelector('dialog[open]')));
  }
  async function openDrawer() {
    let target = drawer();
    if (!target) return false;
    if (drawerIsOpen(target)) return true;
    if (target.localName.includes('-') && !customElements.get(target.localName)) {
      await Promise.race([customElements.whenDefined(target.localName), sleep(1800)]);
    }
    if (typeof target.open === 'function') {
      try { target.open(); } catch (e) { console.warn('[Honeybear V9] theme drawer open failed', e); }
    }
    await sleep(35);
    if (drawerIsOpen(target)) return true;
    // Native header control only, never navigate to /cart.
    const trigger = document.querySelector('header-actions button[aria-controls="cart-drawer"], button[aria-controls="cart-drawer"]');
    if (trigger) trigger.click();
    await sleep(65);
    return drawerIsOpen(drawer());
  }

  function getSections() {
    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map(e => e.dataset.sectionId).filter(Boolean);
    if (drawer()?.localName === 'theme-drawer') sections.push('cart-drawer-section');
    else { sections.push('cart-drawer'); sections.push('cart-icon-bubble'); }
    return [...new Set(sections)].slice(0, 5);
  }

  async function nativePending(data) {
    // New Horizon releases listen for this standard event; older versions do not.
    let module = null;
    try {
      module = await Promise.race([import('@shopify/events').catch(() => null), sleep(350).then(() => null)]);
    } catch (_) { /* legacy theme */ }
    if (!module?.CartLinesUpdateEvent) return null;
    const EventClass = module.CartLinesUpdateEvent;
    const promise = EventClass.createPromise();
    promise.promise.catch(() => {});
    const info = {
      action: 'add', context: 'product',
      lines: [{merchandiseId: String(data.get('id')), quantity: Number(data.get('quantity') || 1)}],
      promise: promise.promise
    };
    document.dispatchEvent(new EventClass(info));
    return {EventClass, promise, quantity: Number(data.get('quantity') || 1)};
  }

  async function updateLegacy(cart, result, quantity) {
    // V2025 Horizon listens to CartUpdateEvent/cart:update, not the new standard event.
    let emitted = false;
    try {
      const module = await Promise.race([import('@theme/events').catch(() => null), sleep(500).then(() => null)]);
      if (module?.CartUpdateEvent) {
        document.dispatchEvent(new module.CartUpdateEvent(cart, 'honeybear-native', {
          itemCount: quantity, source: 'product-form-component', sections: result.sections || {}
        }));
        emitted = true;
      }
    } catch (err) { console.warn('[Honeybear V9] Legacy event unavailable', err); }
    if (!emitted) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true, detail: {data: {itemCount: quantity, source: 'product-form-component', sections: result.sections || {}}}
      }));
    }
    // Fallback for builds that don't observe the event: refresh only the existing inner
    // content. Preserve the native theme-drawer/dialog/header rather than creating UI.
    const target = drawer();
    if (target?.localName === 'theme-drawer') {
      const component = target.querySelector('cart-items-component');
      const id = component?.dataset.sectionId || 'cart-drawer-section';
      let html = result.sections?.[id];
      if (!html) {
        try {
          const r = await fetch(`${endpoint('cart')}?sections=${encodeURIComponent(id)}`, {cache: 'no-store'});
          if (r.ok) html = (await r.json())?.[id];
        } catch (e) { console.warn('[Honeybear V9] Section fetch failed', e); }
      }
      if (typeof html === 'string' && html && component) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('cart-items-component');
        if (incoming) component.replaceWith(incoming);
      }
    } else if (target?.localName === 'cart-drawer' && typeof target.renderContents === 'function') {
      try { target.renderContents(result); } catch (e) { console.warn('[Honeybear V9] Dawn render failed', e); }
    }
    document.querySelectorAll('cart-icon').forEach(icon => {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false);
    });
  }

  async function add(form) {
    if (state.busy) throw new Error('Please finish adding the previous item first.');
    if (!drawer()) throw new Error('The theme cart drawer is missing on this page.');
    const data = new FormData(form);
    const variantId = String(data.get('id') || '');
    const quantity = Number(data.get('quantity') || 1);
    if (!/^\d+$/.test(variantId)) throw new Error('Please select a product option.');
    if (!Number.isInteger(quantity) || quantity < 1) throw new Error('Please choose a valid quantity.');
    data.set('quantity', String(quantity));
    const sections = getSections();
    if (sections.length) data.set('sections', sections.join(','));
    data.set('sections_url', window.location.pathname);
    state.busy = true;
    state.last = {stage: 'starting', error: null, variantId, quantity};
    let pending = null;
    let added = false;
    try {
      pending = drawer()?.localName === 'theme-drawer' ? await nativePending(data) : null;
      state.last.stage = 'sending-cart-add';
      const response = await fetch(endpoint('cart/add.js'), {
        method: 'POST', credentials: 'same-origin',
        headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
        body: data
      });
      const result = await response.json();
      if (!response.ok || result?.status >= 400) {
        throw new Error(result?.description || result?.message || 'Shopify could not add that product.');
      }
      added = true;
      state.last.stage = 'added-opening-drawer';
      // Critical V8 fix: open immediately after successful add, independent of
      // event imports, cart.js, section rendering, or section refresh errors.
      const opened = await openDrawer();
      state.last.drawerOpened = opened;
      if (!opened) console.warn('[Honeybear V9] Added, but native drawer failed to open.');

      let cart = null;
      try {
        state.last.stage = 'refreshing-cart';
        const cartResponse = await fetch(endpoint('cart.js'), {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
        if (!cartResponse.ok) throw new Error('cart.js response was not OK.');
        cart = await cartResponse.json();
        if (pending) {
          pending.promise.resolve({
            cart: pending.EventClass.createCartFromAjaxResponse(cart),
            detail: {didError: false, items: cart.items, source: 'product-form-component',
              sourceId: 'honeybear-v9', itemCount: quantity, sections: result.sections || {}}
          });
        } else {
          await updateLegacy(cart, result, quantity);
        }
      } catch (refreshError) {
        if (pending) pending.promise.reject(refreshError);
        console.error('[Honeybear V9] Item was added and drawer opened, but cart refresh failed:', refreshError);
        state.last.refreshError = String(refreshError);
      }
      state.last.stage = 'complete';
      state.last.cartCount = cart?.item_count ?? null;
      document.dispatchEvent(new CustomEvent('hbl:cart-updated', {detail: {cart, added: result}}));
      return {opened, cart, result};
    } catch (err) {
      if (pending && !added) pending.promise.reject(err);
      state.last.stage = 'error';
      state.last.error = String(err);
      console.error('[Honeybear V9] Add to cart failed at stage:', state.last.stage, err);
      throw err;
    } finally { state.busy = false; }
  }

  function showError(form, message) {
    let status = form.querySelector('[data-hb-cart-error]');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-hb-cart-error', '');
      status.setAttribute('role', 'alert');
      status.style.cssText = 'font: 15px/1.5 inherit; font-size:15px; color:#9b3926; margin:10px 0';
      form.appendChild(status);
    }
    status.textContent = message;
  }

  // Capture all Honeybear custom product forms, so the earlier V5 form submit
  // handlers cannot add the same product again or use a stale bridge reference.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(formSelector)) return;
    if (form.matches('form.hbp3-form') && event.submitter && !event.submitter.matches('[data-hbp3-submit]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    state.handled++;
    const button = event.submitter || form.querySelector('button[type="submit"]');
    const label = button?.querySelector('[data-hbp3-submit-text], [data-hbl-button-label]');
    if (state.busy) return;
    const oldLabel = label?.textContent || '';
    const error = form.querySelector('[data-hb-cart-error]');
    if (error) error.textContent = '';
    if (button) button.disabled = true;
    if (label) label.textContent = 'ADDING…';
    add(form).then(() => {
      if (label) label.textContent = 'ADDED';
    }).catch(err => {
      showError(form, err?.message || 'Unable to add item.');
    }).finally(() => {
      if (button) button.disabled = false;
      if (label) label.textContent = oldLabel;
    });
  }, true);

  function inspect() {
    const target = drawer();
    return {
      bridgeVersion: 9,
      nativeDrawerFound: !!target,
      drawerTag: target?.localName || '(none)',
      drawerID: target?.id || '(none)',
      drawerOpenMethod: typeof target?.open === 'function',
      drawerOpen: drawerIsOpen(target),
      shopFormCount: document.querySelectorAll('form[data-hbl-add]').length,
      productFormCount: document.querySelectorAll('form.hbp3-form').length,
      interceptedSubmits: state.handled,
      busy: state.busy,
      last: {...state.last}
    };
  }
  window.HoneybearNativeCart = Object.freeze({version:9, inspect, testDrawer: openDrawer, add});
})();
